import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation, useSearchParams, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { all_routes } from '../../router/all_routes';
import type { AppDispatch, RootState } from '../../../core/data/redux/store';
import { getMyProfile } from '../../../core/data/redux/employeesSlice';
import { findOneERequisition, approveRejectERequisition, fetchERequisitionMaster } from '../../../core/data/redux/eRequisitionSlice';
import {
  getRequestStatusConfig,
  SapRequestStatus
} from '../../../core/data/constants/requestStatus';
import {
  PRINTER_NOTE,
  VPN_COMPLIANCE,
  alreadyIssuedLabel,
  apiMessage,
  applicantJoining,
  applicantWing,
  equipmentName,
  firewallLabel,
  formTypeLabel,
  formatDisplayDate,
  isApiSuccess,
  isDraftRecord,
  lookupReference,
  lineItemsOf,
  masterOptions,
  normalizeFormType,
  optionLabel,
  pickApproverId,
  readSessionUser,
  resolveEmployee,
  supervisorLabel,
  unwrapMaster,
  unwrapRecord,
} from './eRequisition.shared';

const ERequisitionITDetails: React.FC = () => {
  const routes = all_routes;
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch<AppDispatch>();

  const employeesState = useSelector((state: RootState) => state.employeesState);
  const eState = useSelector((state: RootState) => state.eRequisitionState);
  const sessionUser = readSessionUser();
  const me = resolveEmployee(employeesState, sessionUser);

  const master = unwrapMaster(eState?.masterData);
  const itemOptions = masterOptions(master, 'Item_List_Type');
  const formMasterList = master?.Form_Type || [];
  const vppStatusOptions = masterOptions(master, 'VPP_Status_Type');
  const vppOptions = masterOptions(master, 'User_VPP_Type');
  const mprOptions = masterOptions(master, 'User_MPR_Type');
  const firewallOptions = masterOptions(master, 'Firewall_Status_Type');
  const servicesOptions = masterOptions(master, 'Services_Type');

  const [modalAction, setModalAction] = useState<'A' | 'R' | null>(null);
  const [remarks, setRemarks] = useState('');
  const [allotments, setAllotments] = useState<Record<number, string>>({});
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    dispatch(getMyProfile({}));
    dispatch(fetchERequisitionMaster({}));
    const reference = lookupReference({ ReferenceNumber: id });
    if (reference) dispatch(findOneERequisition({ ReferenceNumber: reference }));
  }, [dispatch, id]);

  const reqData = unwrapRecord(eState?.detailData) || {};
  const itemsList = lineItemsOf(reqData);
  const remarksTrail = Array.isArray(reqData?.ToRemarks?.results)
    ? reqData.ToRemarks.results
    : Array.isArray(reqData?.ToRemarks) ? reqData.ToRemarks : [];
  const formType = normalizeFormType(reqData?.SelectRequestFromType);
  const reqStatus = String(reqData?.RequestStatus || reqData?.Status || reqData?.status || '').trim().toUpperCase();
  const ownRequest = me.empId && String(reqData?.EmpID || '').replace(/^0+/, '') === me.empId.replace(/^0+/, '');

  const isFromUniversalWorklist = Boolean(
    location.state?.isApprover ||
    location.state?.fromUniversalWorklist ||
    searchParams.get('isApprover') === 'true' ||
    searchParams.get('fromWorklist') === 'true'
  );

  const clean = (emp?: any) => String(emp || '').trim().replace(/^0+/, '');
  const isUserApprover = Boolean(
    isFromUniversalWorklist ||
    (me.empId && clean(reqData?.ApproverUserID) === clean(me.empId) && clean(reqData?.EmpID) !== clean(me.empId))
  );

  const isFinalApproved = reqStatus === SapRequestStatus.APPROVED || reqStatus === 'A' || (reqStatus.includes('APPROVED') && !reqStatus.includes('PARTIAL'));
  const isFinalRejected = reqStatus === SapRequestStatus.REJECTED || reqStatus === 'R' || reqStatus.includes('REJECT');
  const isDraft = isDraftRecord(reqData);
  const isPending = !isFinalApproved && !isFinalRejected && !isDraft && (
    reqStatus === 'P' || reqStatus === 'W' || reqStatus === 'T' || reqStatus === 'B' || reqStatus === 'N' ||
    ['in-process', 'pending', 'to be approved', 'to be processed', 'submitted', 'partially approved']
      .some(s => String(reqData?.Status || reqData?.status || '').toLowerCase().includes(s))
  );
  const canApproveReject = isPending && isUserApprover;

  const statusConfig = getRequestStatusConfig(reqData?.RequestStatus || reqData?.Status);
  let displayStatus = isDraft ? 'Draft' : statusConfig.label;
  let displayBadgeClass = isDraft
    ? 'badge bg-warning-subtle text-warning border border-warning-subtle'
    : statusConfig.badgeClass;
  if (isFinalApproved) {
    displayStatus = 'Approved';
    displayBadgeClass = 'badge bg-success-subtle text-success border border-success-subtle';
  } else if (isFinalRejected) {
    displayStatus = 'Rejected';
    displayBadgeClass = 'badge bg-danger-subtle text-danger border border-danger-subtle';
  } else if (reqStatus === 'P') {
    displayStatus = 'Partially Approved';
    displayBadgeClass = 'badge bg-info-subtle text-info border border-info-subtle';
  }

  useEffect(() => {
    const map: Record<number, string> = {};
    itemsList.forEach((it: any, i: number) => {
      map[i] = it.AllotementOrNot || '1';
    });
    setAllotments(map);
  }, [eState.detailData]);

  const handleApproveRejectSubmit = async () => {
    if (modalAction === 'R' && !remarks.trim()) {
      toast.error('Remarks are required to reject');
      return;
    }
    setIsProcessing(true);
    const payload: any = {
      Action: modalAction,
      ReferenceNumber: reqData.ReferenceNumber || id,
      HRemarks: remarks.trim(),
      ApproverUserID: me.empId,
      EmpID: reqData.EmpID,
      SelectRequestFromType: formType,
    };
    if (formType === '00') {
      payload.List_Item = {
        results: itemsList.map((item: any, idx: number) => ({
          ReferenceNumber: item?.ReferenceNumber || reqData.ReferenceNumber || id,
          SerialNumber: item?.SerialNumber || String(idx + 1).padStart(2, '0'),
          AllotementOrNot: allotments[idx] || item?.AllotementOrNot || '',
          Remarks: item?.Remarks,
          Items: item?.Items,
        }))
      };
    }
    try {
      const res = await dispatch(approveRejectERequisition(payload));
      if (approveRejectERequisition.fulfilled.match(res) && isApiSuccess(res.payload)) {
        toast.success(modalAction === 'A' ? 'Application has been approved' : 'Application has been rejected');
        setModalAction(null);
        navigate(routes.universalWorklist || '/universal-worklist');
      } else {
        toast.error(apiMessage(res.payload, 'Action submission failed'));
      }
    } catch (err: any) {
      toast.error(err?.message || 'Error processing action');
    } finally {
      setIsProcessing(false);
    }
  };

  const wing = applicantWing(reqData) || (ownRequest ? me.wing : '');
  const joining = applicantJoining(reqData) || (ownRequest ? formatDisplayDate(me.dateOfJoining) : '');
  const dob = formatDisplayDate(reqData.DateOfBirth || reqData.dateOfBirth || (ownRequest ? me.dateOfBirth : ''));

  const baseDetailsGrid = [
    { label: 'Reference Number', value: reqData.ReferenceNumber || id || '-' },
    { label: 'Approver ID', value: reqData.ApproverUserID || '-' },
    { label: 'Approver Name', value: reqData.ApproverName || '-' },
    { label: 'Employee Name', value: reqData.EmployeeName || reqData.EmpName || (ownRequest ? me.employeeName : '-') || '-' },
    { label: 'Employee No.', value: reqData.EmpID || '-' },
    { label: 'Date of Joining', value: joining || '-' },
    { label: 'Designation', value: reqData.Designation || (ownRequest ? me.designation : '') || '-' },
    { label: 'Department', value: reqData.Department || (ownRequest ? me.department : '') || '-' },
    { label: 'Wing (Project/O&M)', value: wing || '-' },
    { label: 'Mobile No.', value: reqData.ContactNumber || '-' },
    { label: 'Official Email', value: reqData.EmailID || '-' },
    { label: 'Office Location', value: reqData.OfficeLocation || '-' },
    { label: 'Request Form Type', value: formTypeLabel(formType, formMasterList, reqData.SelectRequestFromTypeText) },
  ];

  const detailLoading = eState.action === 'findOne' && eState.loading === 'pending' && !reqData.ReferenceNumber;

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
          <div>
            <h4 className="page-title mb-0 text-dark fw-bold">IT Requisition Details</h4>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item"><Link to={routes.employeeDashboard || '#'}>Dashboard</Link></li>
                <li className="breadcrumb-item"><Link to={routes.eRequisitionIT.list}>E-Requisition (IT)</Link></li>
                <li className="breadcrumb-item active">Details</li>
              </ol>
            </nav>
          </div>
          <div className="d-flex gap-2">
            <button className="btn btn-outline-secondary btn-sm px-3" onClick={() => navigate(-1)}>
              <i className="ti ti-arrow-left me-1"></i>Back to List
            </button>
            {isDraft && (
              <Link
                to={routes.eRequisitionIT.edit.replace(':id', String(reqData.ReferenceNumber || id))}
                state={{ draftRequest: reqData }}
                className="btn btn-warning btn-sm px-3"
              >
                <i className="ti ti-pencil me-1"></i>Continue Draft
              </Link>
            )}
            {canApproveReject && (
              <>
                <button className="btn btn-outline-danger btn-sm px-3" onClick={() => { setModalAction('R'); setRemarks(''); }}>
                  <i className="ti ti-x me-1"></i>Reject
                </button>
                <button className="btn btn-success btn-sm px-3" onClick={() => { setModalAction('A'); setRemarks(''); }}>
                  <i className="ti ti-check me-1"></i>Approve
                </button>
              </>
            )}
          </div>
        </div>

        {detailLoading ? (
          <div className="text-center py-5"><div className="spinner-border text-danger" role="status"></div></div>
        ) : (
          <div className="card border-0 shadow-sm mb-4">
            <div className="card-header bg-white border-bottom py-3 d-flex align-items-center justify-content-between">
              <h5 className="mb-0 fw-bold text-dark"><i className="ti ti-file-text me-2 text-danger"></i>Requisition #{reqData.ReferenceNumber || id}</h5>
              <span className={`badge px-3 py-1.5 fs-12 rounded-pill ${displayBadgeClass}`}>{displayStatus}</span>
            </div>
            <div className="card-body p-4">
              <div className="bg-light-subtle rounded-3 p-3 border mb-4">
                <h6 className="fw-bold text-dark mb-3 fs-14"><i className="ti ti-user-check me-2 text-danger"></i>Application Info</h6>
                <div className="row g-3">
                  {baseDetailsGrid.map((f, idx) => (
                    <div className="col-md-3 col-sm-6" key={idx}>
                      <div className="p-2 rounded bg-white border h-100">
                        <span className="text-muted small d-block mb-1">{f.label}</span>
                        <span className="fw-semibold text-dark d-block">{f.value}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {formType === '00' && (
                <div className="mb-4">
                  <h6 className="fw-bold text-dark mb-2">IT Equipment Requisition Form</h6>
                  <div className="alert alert-warning py-2 px-3 mb-3">
                    <span className="text-danger fw-bold">* </span>{PRINTER_NOTE}
                  </div>
                  <div className="table-responsive border rounded-3">
                    <table className="table table-hover align-middle mb-0">
                      <thead className="table-light">
                        <tr>
                          <th style={{ width: '90px' }}>Sr. No.</th>
                          <th>Items</th>
                          <th>Remarks</th>
                          <th style={{ width: '180px' }}>Allotment</th>
                        </tr>
                      </thead>
                      <tbody>
                        {itemsList.length === 0 ? (
                          <tr><td colSpan={4} className="text-center text-muted py-3">No line items added</td></tr>
                        ) : itemsList.map((it: any, idx: number) => {
                          const serial = it.SerialNumber || String(idx + 1).padStart(2, '0');
                          const currentAllot = allotments[idx] || it.AllotementOrNot || '';
                          return (
                            <tr key={idx}>
                              <td className="fw-bold">{serial}</td>
                              <td>{equipmentName(it.Items, itemOptions)}</td>
                              <td>{it.Remarks || '-'}</td>
                              <td>
                                {canApproveReject ? (
                                  <select
                                    className="form-select form-select-sm"
                                    value={currentAllot}
                                    onChange={e => setAllotments(prev => ({ ...prev, [idx]: e.target.value }))}
                                  >
                                    <option value="">Select allotment</option>
                                    <option value="1">Allotted</option>
                                    <option value="2">Not Allotted</option>
                                  </select>
                                ) : (
                                  <span className={`badge ${currentAllot === '1' ? 'bg-success-subtle text-success' : currentAllot === '2' ? 'bg-secondary-subtle text-secondary' : 'bg-light text-dark'}`}>
                                    {currentAllot === '1' ? 'Allotted' : currentAllot === '2' ? 'Not Allotted' : '-'}
                                  </span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  <div className="row g-3 mt-2">
                    <div className="col-md-6">
                      <div className="p-3 border rounded-3 bg-light-subtle">
                        <div className="text-muted small mb-1">Only for requirement of Supervisors: PCs issued to Supervisors are not more than 50% of Supervisors working under concerned HOD</div>
                        <span className="fw-semibold">{supervisorLabel(reqData.SupervisorUnderHOD)}</span>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="p-3 border rounded-3 bg-light-subtle">
                        <div className="text-muted small mb-1">Already Issued IT assets (if any)</div>
                        <span className="fw-semibold">{alreadyIssuedLabel(reqData, isUserApprover)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {formType === '11' && (
                <div className="bg-light-subtle rounded-3 p-3 border mb-4">
                  <h6 className="fw-bold text-dark mb-3">Eoffice form</h6>
                  <div className="row g-3">
                    <div className="col-md-4"><span className="text-muted small d-block">Date of Birth</span><span className="fw-semibold">{dob || '-'}</span></div>
                    <div className="col-md-4"><span className="text-muted small d-block">Type of Appointment</span><span className="fw-semibold">{reqData.TypeOfAppointment || 'Direct Recruit'}</span></div>
                    <div className="col-md-4"><span className="text-muted small d-block">From date working in current department</span><span className="fw-semibold">{formatDisplayDate(reqData.WorkingFromDate) || '-'}</span></div>
                  </div>
                </div>
              )}

              {formType === '12' && (
                <div className="bg-light-subtle rounded-3 p-3 border mb-4">
                  <h6 className="fw-bold text-dark mb-3">VPP/MPR</h6>
                  <div className="row g-3">
                    <div className="col-md-4"><span className="text-muted small d-block">Authorization for VPP</span><span className="fw-semibold">{reqData.UserProfileForVPPText || optionLabel(vppOptions, reqData.UserProfileForVPP)}</span></div>
                    <div className="col-md-4"><span className="text-muted small d-block">Authorization for MPR</span><span className="fw-semibold">{reqData.UserProfileForMPRText || optionLabel(mprOptions, reqData.UserProfileForMPR)}</span></div>
                    <div className="col-md-4"><span className="text-muted small d-block">Status</span><span className="fw-semibold">{reqData.VPPStatusText || optionLabel(vppStatusOptions, reqData.VPPStatus)}</span></div>
                  </div>
                </div>
              )}

              {formType === '13' && (
                <div className="bg-light-subtle rounded-3 p-3 border mb-4">
                  <h6 className="fw-bold text-dark mb-3">SSL VPN</h6>
                  <div className="row g-3">
                    <div className="col-md-4"><span className="text-muted small d-block">Operating System Name and Version</span><span className="fw-semibold">{reqData.OperatingSystemVersion || '-'}</span></div>
                    <div className="col-md-4"><span className="text-muted small d-block">Antivirus (Name and Version)</span><span className="fw-semibold">{reqData.Antivirus || '-'}</span></div>
                    <div className="col-md-4"><span className="text-muted small d-block">Firewall status</span><span className="fw-semibold">{firewallLabel(reqData.FirewallStatus, reqData.FirewallStatusText)}</span></div>
                    <div className="col-md-4"><span className="text-muted small d-block">System MAC address</span><span className="fw-semibold">{reqData.MacAddress || '-'}</span></div>
                    <div className="col-md-4"><span className="text-muted small d-block">Details of server you need to access</span><span className="fw-semibold">{reqData.DetailsOfServer || '-'}</span></div>
                    <div className="col-md-4"><span className="text-muted small d-block">Valid From</span><span className="fw-semibold">{formatDisplayDate(reqData.AccessDurationFrom) || '-'}</span></div>
                    <div className="col-md-4"><span className="text-muted small d-block">To</span><span className="fw-semibold">{formatDisplayDate(reqData.AccessDurationTo) || '-'}</span></div>
                  </div>
                  <div className="mt-3">
                    <div className="fw-semibold mb-2">Compliance agreement</div>
                    <ul className="mb-0 small">
                      {VPN_COMPLIANCE.map(item => <li key={item}>{item}</li>)}
                    </ul>
                  </div>
                </div>
              )}

              {formType === '14' && (
                <div className="bg-light-subtle rounded-3 p-3 border mb-4">
                  <h6 className="fw-bold text-dark mb-3">Third-Party Email Access Request</h6>
                  <div className="row g-3">
                    <div className="col-md-6"><span className="text-muted small d-block">Services</span><span className="fw-semibold">{reqData.ServicesText || optionLabel(servicesOptions, reqData.Services)}</span></div>
                    <div className="col-md-6"><span className="text-muted small d-block">System IP Address</span><span className="fw-semibold">{reqData.SystemIPAddress || '-'}</span></div>
                    <div className="col-12"><span className="text-muted small d-block">Any other remarks</span><span className="fw-semibold">{reqData.ORemarks || reqData.Remarks || '-'}</span></div>
                  </div>
                </div>
              )}

              <div className="p-3 border rounded-3 bg-white">
                <h6 className="fw-bold text-dark mb-3 fs-14"><i className="ti ti-history me-2 text-danger"></i>Approver History</h6>
                {remarksTrail.length > 0 ? (
                  <div className="table-responsive border rounded-3">
                    <table className="table table-hover align-middle mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>Level</th>
                          <th>Approver Name</th>
                          <th>Approver ID</th>
                          <th>Timestamp</th>
                          <th>Remarks</th>
                        </tr>
                      </thead>
                      <tbody>
                        {remarksTrail.map((rem: any, rIdx: number) => {
                          const approverId = pickApproverId(rem, reqData.EmpID);
                          const name = String(rem.ApproverUserID || '') === '00000000'
                            ? 'Administrator'
                            : (rem.ApproverName || '-');
                          return (
                            <tr key={rIdx}>
                              <td>L{rem.ApproverLevel || rIdx + 1}</td>
                              <td className="fw-semibold">{name}</td>
                              <td>{approverId || '-'}</td>
                              <td>{formatDisplayDate(rem.TimeStamp) || rem.TimeStamp || '-'}</td>
                              <td>{rem.HRemarks || rem.Remarks || '-'}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-muted small mb-0">{reqData.HRemarks || reqData.Remarks || 'No remarks recorded.'}</p>
                )}
              </div>
            </div>
          </div>
        )}

        {modalAction && (
          <div className="modal show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: '480px' }}>
              <div className="modal-content border-0 shadow">
                <div className={`modal-header py-2 px-3 ${modalAction === 'A' ? 'bg-success-subtle text-success' : 'bg-danger-subtle text-danger'}`}>
                  <h6 className="modal-title fw-bold mb-0">{modalAction === 'A' ? 'Approve IT Requisition' : 'Reject IT Requisition'}</h6>
                  <button type="button" className="btn-close" onClick={() => setModalAction(null)}></button>
                </div>
                <div className="modal-body p-3">
                  <label className="form-label fw-semibold small">Approver ID</label>
                  <input type="text" className="form-control form-control-sm mb-2" value={me.empId} readOnly />
                  <label className="form-label fw-semibold small">Remarks {modalAction === 'R' && <span className="text-danger">*</span>}</label>
                  <textarea className="form-control" rows={3} value={remarks} onChange={e => setRemarks(e.target.value)} placeholder="Write something..." />
                </div>
                <div className="modal-footer py-2">
                  <button type="button" className="btn btn-light btn-sm" onClick={() => setModalAction(null)} disabled={isProcessing}>Cancel</button>
                  <button type="button" className={`btn btn-sm text-white ${modalAction === 'A' ? 'btn-success' : 'btn-danger'}`} onClick={handleApproveRejectSubmit} disabled={isProcessing}>
                    {isProcessing ? 'Submitting...' : modalAction === 'A' ? 'Approve' : 'Reject'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ERequisitionITDetails;
